import React, { useCallback, useState, useRef, useMemo, useEffect } from 'react'
import { useMessageInputContext, useChannelStateContext, useChatContext } from 'stream-chat-react'
import type { MessageResponse, Attachment, SendFileAPIResponse } from 'stream-chat'
// import ReactDOM from 'react-dom'

import { RichTextInput, type SubmitPayload, type UploadedAttachment } from './RichTextInput'
import type { StreamChatGenerics } from '../../types'
import { isGroupChannel } from '../../utils/channelHelpers'
// import { isAdvisorRole } from 'utils/helper'
import { ReactComponent as MicIcon } from 'assets/svgs/microphone-icon.svg'
import { ReactComponent as TrashIcon } from 'assets/svgs/trash.svg'
import { ReactComponent as FileUploadIcon } from 'assets/svgs/file-upload-icon.svg'
import { VoiceRecorder } from '../VoiceRecorder'
import { useGetTeammember } from 'hooks/useGetTeammember'
import CommonApi from 'api/common'
import { getTenantId } from 'utils/getTenantId'

// const Portal = ({ children }) => {
//   if (typeof window === 'undefined') return null
//   return ReactDOM.createPortal(children, document.body)
// }

const mapUploadedAttachmentToStream = <T extends StreamChatGenerics>(
  uploaded: UploadedAttachment
): Attachment<T> => {
  const base = {
    mime_type: uploaded.mimeType,
    file_size: uploaded.fileSize,
    title: uploaded.fileName,
    name: uploaded.fileName,
  } as Attachment<T>

  if (uploaded.type === 'image') {
    return {
      ...base,
      type: 'image',
      image_url: uploaded.url,
      thumb_url: uploaded.url,
    }
  }

  return {
    ...base,
    type: 'file',
    asset_url: uploaded.url,
  }
}

interface RichTextMessagingInputProps {
  showChannelCreate?: boolean
  initialContent?: string
  onSubmitOverride?: (
    richTextContent: string,
    options?: {
      attachments?: Attachment<StreamChatGenerics>[]
    }
  ) => Promise<void> | void
  initialAttachments?: Attachment<StreamChatGenerics>[]
  groupFormatting?: boolean // New prop to enable grouped formatting toolbar
  /** When in edit mode (onSubmitOverride set), render these on the same line as the formatting toolbar (e.g. Cancel / Save). */
  editModeToolbarActions?: React.ReactNode
}

const RichTextMessagingInput = ({
  showChannelCreate = false,
  initialContent,
  onSubmitOverride,
  initialAttachments,
  groupFormatting = false,
  editModeToolbarActions,
}: RichTextMessagingInputProps) => {
  const messageInput = useMessageInputContext<StreamChatGenerics>()
  const { channel } = useChannelStateContext<StreamChatGenerics>()
  const { client } = useChatContext<StreamChatGenerics>()

  const [quotedMessage, setQuotedMessage] = useState<MessageResponse<StreamChatGenerics> | null>(
    null
  )
  const [isRecording, setIsRecording] = useState(false)
  const [transcript, setTranscript] = useState<string>('')
  const [voiceDurationSeconds, setVoiceDurationSeconds] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const teamMembers = useGetTeammember()
  const isEditMode = typeof onSubmitOverride === 'function'
  const [editModeAttachments, setEditModeAttachments] = useState<Attachment<StreamChatGenerics>[]>(
    () => {
      if (!initialAttachments) return []

      // Deduplicate attachments by URL to prevent duplicate previews in edit mode
      return initialAttachments.reduce((acc, current) => {
        const currentUrl =
          current.og_scrape_url ||
          current.title_link ||
          current.asset_url ||
          current.image_url ||
          current.file_url

        const isDuplicate = acc.some((item) => {
          const itemUrl =
            item.og_scrape_url ||
            item.title_link ||
            item.asset_url ||
            item.image_url ||
            item.file_url
          return currentUrl && itemUrl && itemUrl === currentUrl
        })

        if (!isDuplicate) {
          acc.push(current)
        }
        return acc
      }, [] as Attachment<StreamChatGenerics>[])
    }
  )

  useEffect(() => {
    if (isEditMode && initialAttachments) {
      // Deduplicate attachments by URL to prevent duplicate previews in edit mode
      const uniqueAttachments = initialAttachments.reduce((acc, current) => {
        // Get the identifying URL for this attachment
        const currentUrl =
          current.og_scrape_url ||
          current.title_link ||
          current.asset_url ||
          current.image_url ||
          current.file_url

        // Check if we already have an attachment with this URL
        const isDuplicate = acc.some((item) => {
          const itemUrl =
            item.og_scrape_url ||
            item.title_link ||
            item.asset_url ||
            item.image_url ||
            item.file_url
          return currentUrl && itemUrl && itemUrl === currentUrl
        })

        if (!isDuplicate) {
          acc.push(current)
        }
        return acc
      }, [] as Attachment<StreamChatGenerics>[])

      setEditModeAttachments(uniqueAttachments)
    } else if (isEditMode) {
      setEditModeAttachments([])
    }
  }, [initialAttachments, isEditMode])

  // Debug logging

  // const isAdvisor = isAdvisorRole()

  // Determine placeholder text based on chat type
  const otherMembers = Object.values(channel?.state?.members || {}).filter(
    (member) => member.user?.id !== client?.user?.id
  )

  // Determine if this is a 1-1 chat (exactly one other member) or group chat
  const isOneOnOneChat = !isGroupChannel(channel)

  // Determine mentionable members based on chat type
  // For 1-1 chats: show all team members + advisors
  // For group chats: only show members in the group
  const mentionableMembers = useMemo(() => {
    // For 1-1 chats: use all team members (includes team members + advisors)
    if (isOneOnOneChat) {
      return teamMembers
    }

    // For group chats: only use channel members
    if (!channel || !client?.user?.id) return []

    const membersObj = channel.state?.members || {}
    const members = Object.values(membersObj)
      .filter((member) => member.user?.id !== client.user?.id)
      .filter((member) => member.user?.id && member.user?.firstName && member.user?.lastName)
      .map((member) => ({
        id: member.user?.id || '',
        firstName: (member.user?.firstName as string) || '',
        lastName: (member.user?.lastName as string) || '',
      }))

    return members
  }, [isOneOnOneChat, teamMembers, channel, client?.user?.id])

  // Memoize placeholder text to ensure it's stable and doesn't cause unnecessary re-renders
  const placeholderText = useMemo(() => {
    if (showChannelCreate) {
      return 'Start a conversation...'
    }

    // One-to-one chat: show the other member's name
    if (otherMembers.length === 1) {
      const member = otherMembers[0]
      return `Message ${member.user?.name || member.user?.id || 'user'}...`
    }

    // Group chat: prefer the channel/group name if available
    if (otherMembers.length > 1) {
      const groupName =
        (channel?.data && (channel.data as Record<string, unknown>).name) ||
        (channel as any)?.data?.name

      if (typeof groupName === 'string' && groupName.trim().length > 0) {
        return `Message ${groupName.trim()}...`
      }

      return `Message ${otherMembers.length} people...`
    }

    return 'Type a message...'
  }, [showChannelCreate, otherMembers, channel])

  const handleSubmit = useCallback(
    async (richTextContent: string, uploadedAttachments: UploadedAttachment[] = []) => {
      const uploadedAsStream = uploadedAttachments.map((attachment) =>
        mapUploadedAttachmentToStream<StreamChatGenerics>(attachment)
      )

      // CRITICAL: If a custom submit handler is provided (e.g., for editing), use ONLY that path
      // This ensures edits from the EditMessageForm NEVER trigger Stream's send logic
      if (typeof onSubmitOverride === 'function') {
        try {
          const attachments = [...editModeAttachments, ...uploadedAsStream]
          await onSubmitOverride(richTextContent, { attachments })
          // After successful edit, clear any local state
          messageInput.setText?.('')
          setTranscript('')
          setVoiceDurationSeconds(null)
          // Do NOT clear editing state here; the override handler does it
          return // Exit immediately - no other paths should execute
        } catch (error) {
          console.error('Error in override submit:', error)
          return
        }
      }

      // Hard guard: if global edit lock is set and no override is provided, do not send
      const isGloballyEditing = (window as unknown as { __CHAT_EDITING__?: boolean })
        .__CHAT_EDITING__
      if (isGloballyEditing) {
        console.warn('Blocked send while editing an existing message (no override)')
        return
      }

      // Guard: If Stream MessageInput is in editing mode, update the message instead of sending a new one
      try {
        const editingId = (messageInput as any)?.editing?.id as string | undefined
        if (editingId && client) {
          const cid = channel?.cid
          const editingMessage = channel?.state?.messages?.find(
            (m: { id?: string; pinned?: boolean }) => m.id === editingId
          )
          await client.updateMessage({
            id: editingId,
            text: richTextContent || '',
            cid: cid || undefined,
            pinned: !!editingMessage?.pinned,
          })
          messageInput.setText?.('')
          setTranscript('')
          setVoiceDurationSeconds(null)
          Object.keys(messageInput.fileUploads || {}).forEach((uploadId) => {
            messageInput.removeFile?.(uploadId)
          })
          Object.keys(messageInput.imageUploads || {}).forEach((uploadId) => {
            messageInput.removeImage?.(uploadId)
          })
          messageInput.clearEditingState?.()
          return
        }
      } catch (err) {
        console.error('Error updating message in edit mode:', err)
        // fall through to normal send if update fails
      }
      // If we have a transcript, send message with custom data directly
      if ((transcript || voiceDurationSeconds !== null) && channel) {
        try {
          // Convert file uploads to attachments format
          const attachments: Attachment<StreamChatGenerics>[] = []

          // Add file uploads
          Object.values(messageInput.fileUploads || {}).forEach((upload) => {
            if (upload.state === 'finished' && upload.file) {
              attachments.push({
                type: 'file',
                asset_url: upload.url,
                mime_type: upload.file.type,
                file_size: upload.file.size,
                title: upload.file.name,
              })
            }
          })

          // Add image uploads
          Object.values(messageInput.imageUploads || {}).forEach((upload) => {
            if (upload.state === 'finished' && upload.file) {
              attachments.push({
                type: 'image',
                image_url: upload.url,
                mime_type: upload.file.type,
                file_size: upload.file.size,
                title: upload.file.name,
              })
            }
          })

          attachments.push(...uploadedAsStream)

          const messageData = {
            text: richTextContent || '',
            attachments: attachments,
            // If replying in a thread, ensure parent_id is set so it appears under the thread
            ...(messageInput.parent?.id
              ? { parent_id: messageInput.parent.id, show_in_channel: false }
              : {}),
            custom: {
              transcript: transcript,
              voiceDurationSeconds: voiceDurationSeconds,
              richText: true,
              richTextHtml: richTextContent,
              mentions: [],
            },
          }

          await channel.sendMessage(messageData)

          // Clear the input and transcript after successful send
          messageInput.setText('')
          setTranscript('')
          setVoiceDurationSeconds(null)

          // Clear all uploaded files
          Object.keys(messageInput.fileUploads || {}).forEach((uploadId) => {
            if (messageInput.removeFile) {
              messageInput.removeFile(uploadId)
            }
          })

          Object.keys(messageInput.imageUploads || {}).forEach((uploadId) => {
            if (messageInput.removeImage) {
              messageInput.removeImage(uploadId)
            }
          })

          // Clear any other editing state
          if (messageInput.clearEditingState) {
            messageInput.clearEditingState()
          }
        } catch (error) {
          console.error('Error sending message:', error)
        }
      } else if (channel) {
        // Send a normal rich text message directly via Stream channel
        try {
          // Build attachments array from messageInput file and image uploads
          const attachments: Attachment<StreamChatGenerics>[] = []

          // Add file uploads
          Object.values(messageInput.fileUploads || {}).forEach((upload) => {
            if (upload.state === 'finished' && upload.file) {
              attachments.push({
                type: 'file',
                asset_url: upload.url,
                mime_type: upload.file.type,
                file_size: upload.file.size,
                title: upload.file.name,
              })
            }
          })

          // Add image uploads
          Object.values(messageInput.imageUploads || {}).forEach((upload) => {
            if (upload.state === 'finished' && upload.file) {
              attachments.push({
                type: 'image',
                image_url: upload.url,
                mime_type: upload.file.type,
                file_size: upload.file.size,
                title: upload.file.name,
              })
            }
          })

          attachments.push(...uploadedAsStream)

          await channel.sendMessage({
            text: richTextContent || '',
            html: richTextContent,
            attachments: attachments as any,
            // If replying in a thread, ensure parent_id is set so it appears under the thread
            ...(messageInput.parent?.id
              ? { parent_id: messageInput.parent.id, show_in_channel: false }
              : {}),
            custom: { richText: true, richTextHtml: richTextContent, mentions: [] },
          })

          // Clear the input after successful send
          messageInput.setText('')
          setTranscript('')
          setVoiceDurationSeconds(null)

          // Clear all uploaded files
          Object.keys(messageInput.fileUploads || {}).forEach((uploadId) => {
            if (messageInput.removeFile) {
              messageInput.removeFile(uploadId)
            }
          })

          Object.keys(messageInput.imageUploads || {}).forEach((uploadId) => {
            if (messageInput.removeImage) {
              messageInput.removeImage(uploadId)
            }
          })

          // Clear any other editing state
          if (messageInput.clearEditingState) {
            messageInput.clearEditingState()
          }
        } catch (err) {
          console.error('Error sending message:', err)
        }
      }
    },
    [
      messageInput,
      channel,
      transcript,
      voiceDurationSeconds,
      onSubmitOverride,
      client,
      editModeAttachments,
    ]
  )

  const handleTextChange = useCallback(
    (text: string) => {
      messageInput.setText?.(text)
    },
    [messageInput]
  )

  // Wrapper function to adapt SubmitPayload to the handleSubmit signature
  const handleSubmitWrapper = useCallback(
    async (payload: SubmitPayload) => {
      await handleSubmit(payload.html, payload.attachments)
    },
    [handleSubmit]
  )

  const handleFileUpload = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  // no separate upload helper; call uploadNewFiles directly where needed

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (files && files.length > 0) {
        messageInput.uploadNewFiles?.(Array.from(files))
      }
    },
    [messageInput]
  )

  // Handle voice message
  const handleVoiceMessage = useCallback(
    async (audioBlob: Blob, transcript?: string, durationSeconds?: number) => {
      try {
        // Create a File object from the Blob
        const audioFile = new File([audioBlob], `voice-message-${Date.now()}.webm`, {
          type: 'audio/webm;codecs=opus',
        })

        // Upload the voice file using the existing file upload mechanism
        messageInput.uploadNewFiles?.([audioFile])

        // Store transcript and duration in state
        if (transcript) setTranscript(transcript)
        if (typeof durationSeconds === 'number') setVoiceDurationSeconds(durationSeconds)

        // Voice message processed successfully - hide recorder
        setIsRecording(false)
      } catch (error) {
        console.error('Error processing voice message:', error)
        setIsRecording(false)
      }
    },
    [messageInput]
  )

  // Handle voice recorder cancel
  const handleVoiceCancel = useCallback(() => {
    setIsRecording(false)
  }, [])

  // Start recording
  const startVoiceRecording = useCallback(() => {
    setIsRecording(true)
  }, [])

  const asString = (value: unknown) => (typeof value === 'string' ? value : undefined)

  const getAttachmentLabel = (attachment: Attachment<StreamChatGenerics>) => {
    return (
      asString(attachment.title) ||
      asString(attachment.name) ||
      asString(attachment.image_url) ||
      asString(attachment.asset_url) ||
      asString(attachment.file_url) ||
      'Attachment'
    )
  }

  const handleRemoveExistingAttachment = (indexToRemove: number) => {
    setEditModeAttachments((prev) => prev.filter((_, index) => index !== indexToRemove))
  }

  return (
    <div className="messaging-input-container">
      {/* File input (hidden) */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileChange}
        className="hidden"
        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.csv,.xlsx,.xls"
      />

      {/* Show existing attachments when editing */}
      {isEditMode && editModeAttachments.length > 0 && (
        <div className="mb-3 space-y-2">
          {editModeAttachments.map((attachment, index) => {
            const isImage = attachment.type === 'image'
            const primaryImage =
              typeof attachment.image_url === 'string' ? attachment.image_url : undefined
            const fallbackImage =
              typeof attachment.thumb_url === 'string' ? attachment.thumb_url : undefined
            const previewSrc = primaryImage || fallbackImage
            const keyValue =
              (typeof attachment.id === 'string' && attachment.id) ||
              (typeof attachment.asset_url === 'string' && attachment.asset_url) ||
              (typeof attachment.image_url === 'string' && attachment.image_url) ||
              `${index}`

            return (
              <div
                key={keyValue}
                className="flex items-center gap-3 p-3 border border-app-grey-25 rounded-xl bg-app-grey-5"
              >
                <div className="flex-shrink-0">
                  {isImage && previewSrc ? (
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-white overflow-hidden border border-app-grey-20">
                      <img
                        src={previewSrc as string}
                        alt={getAttachmentLabel(attachment)}
                        className="object-cover w-full h-full"
                      />
                    </div>
                  ) : (
                    <FileUploadIcon className="w-5 h-5 text-app-grey-60" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-app-grey-80 truncate">
                    {getAttachmentLabel(attachment)}
                  </p>
                  {attachment.mime_type && (
                    <p className="text-xs text-app-grey-55 mt-0.5">{attachment.mime_type}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveExistingAttachment(index)}
                  className="flex-shrink-0 p-1 text-app-grey-60 hover:text-red-500 transition-colors"
                  aria-label="Remove attachment"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            )
          })}
        </div>
      )}

      {/* Quoted message display */}
      {quotedMessage && (
        <div className="mb-2 p-3 bg-gray-50 border-l-4 border-app-primary-60 rounded">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <span className="font-medium">
                Replying to {quotedMessage.user?.name || 'Unknown'}
              </span>
              <p className="mt-1 text-gray-500 line-clamp-2">
                {(quotedMessage.text ?? '').substring(0, 100)}
                {(quotedMessage.text?.length ?? 0) > 100 ? '...' : ''}
              </p>
            </div>
            <button
              onClick={() => setQuotedMessage(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Voice recorder - show above input when active */}
      {isRecording && (
        <VoiceRecorder
          onVoiceMessage={handleVoiceMessage}
          onCancel={handleVoiceCancel}
          className="mb-3"
          autoStart={true}
        />
      )}

      {/* Rich text input */}
      <RichTextInput
        placeholder={placeholderText}
        onSubmit={handleSubmitWrapper}
        onTextChange={handleTextChange}
        disabled={false}
        onAttachClick={handleFileUpload}
        initialContent={initialContent || ''}
        hasVoiceMessage={voiceDurationSeconds !== null}
        groupFormatting={groupFormatting}
        initialMentions={(mentionableMembers || []).map((m) => ({
          id: m.id,
          label: `${m.firstName || ''} ${m.lastName || ''}`.trim() || 'User',
          email: '',
          firstName: m.firstName || '',
          lastName: m.lastName || '',
        }))}
        mentionProvider={(query: string) => {
          const q = (query || '').toLowerCase()
          const list = (mentionableMembers || []).map((m) => ({
            id: m.id,
            label: `${m.firstName || ''} ${m.lastName || ''}`.trim() || 'User',
            email: '',
            firstName: m.firstName || '',
            lastName: m.lastName || '',
          }))
          return list.filter((i) => i.label.toLowerCase().includes(q))
        }}
        // Upload adapter uploads to Stream and returns unified attachment
        uploadAdapter={async (
          file: File,
          onProgress?: (progress: number) => void
        ): Promise<UploadedAttachment> => {
          if (!channel) throw new Error('Channel not available')

          const reportProgress = (value: number) => {
            if (onProgress) {
              const clamped = Math.min(100, Math.max(0, value))
              onProgress(clamped)
            }
          }

          // System Upload Logic
          const uploadToSystem = async () => {
            try {
              reportProgress(5)
              const tenantId = getTenantId()
              const userId = client?.user?.id
              if (!userId) throw new Error('User ID not found')

              // Get Signed URL
              const res = await CommonApi.getSignedUrl(file.type, file.name)
              reportProgress(10)
              const signedUrlData = JSON.parse(res.getSignedUrl.data)

              // Upload to S3 with progress
              await new Promise<void>((resolve, reject) => {
                const xhr = new XMLHttpRequest()
                xhr.open('PUT', signedUrlData.signedUrl)
                xhr.setRequestHeader('Content-Type', file.type)
                xhr.upload.onprogress = (e) => {
                  if (e.lengthComputable) {
                    const percent = (e.loaded / e.total) * 100
                    reportProgress(10 + percent * 0.8)
                  }
                }
                xhr.onload = () => {
                  if (xhr.status === 200) resolve()
                  else reject(new Error('System upload failed'))
                }
                xhr.onerror = () => reject(new Error('System upload failed'))
                xhr.send(file)
              })

              // Construct File URL (mimicking helper logic)
              const fileUrl = `https://${process.env.REACT_APP_MEDIA_BUCKET}/uploads/${
                signedUrlData.signedUrl.split('/uploads/')[1].split('?')[0]
              }`

              // Save to Backend
              await CommonApi.saveUserFile({
                sendNotification: false,
                files: [
                  {
                    name: file.name,
                    userId,
                    url: fileUrl,
                    id: signedUrlData.id,
                    type: file.type,
                    size: file.size.toString(),
                    tenantId,
                  },
                ],
              })
              reportProgress(100)
            } catch (e) {
              console.error('System upload failed', e)
              // We continue even if system upload fails, as chat is primary
            }
          }

          // Stream Upload Logic
          const uploadToStream = async () => {
            let res: SendFileAPIResponse
            if (file.type?.startsWith('image/') && (channel as any).sendImage) {
              res = await channel.sendImage(file)
            } else if (channel.sendFile) {
              res = await channel.sendFile(file)
            } else {
              throw new Error('Channel file upload API not available')
            }
            return res?.file || res?.thumb_url || ''
          }

          // Execute both uploads
          // Note: Stream upload doesn't support progress hook easily, so we rely on system upload for progress
          const [streamUrl] = await Promise.all([uploadToStream(), uploadToSystem()])

          return {
            type: file.type?.startsWith('image/') ? 'image' : 'file',
            url: streamUrl,
            fileName: file.name,
            mimeType: file.type,
            fileSize: file.size,
          }
        }}
        rightActions={
          isEditMode && editModeToolbarActions ? (
            editModeToolbarActions
          ) : (
            <>
              {/* Mic button to match design */}
              {!isRecording && (
                <button
                  type="button"
                  onClick={startVoiceRecording}
                  className="w-9 h-9 rounded-lg text-app-grey-60 hover:text-app-black-80 hover:bg-app-grey-20 flex items-center justify-center transition-colors"
                  title="Record voice message"
                >
                  <MicIcon className="w-5 h-5" />
                </button>
              )}
            </>
          )
        }
      />

      {/* Action buttons */}
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-2">
          {/* Secondary actions kept minimal; attach moved into input toolbar */}
        </div>

        {/* File upload indicators */}
        {(() => {
          const fileUploadCount = messageInput.fileUploads
            ? Object.keys(messageInput.fileUploads).length
            : 0
          const imageUploadCount = messageInput.imageUploads
            ? Object.keys(messageInput.imageUploads).length
            : 0
          const totalUploads = fileUploadCount + imageUploadCount

          return totalUploads > 0 ? (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>{totalUploads} file(s) attached</span>
              <button
                type="button"
                onClick={() => {
                  // Clear all uploaded files
                  Object.keys(messageInput.fileUploads || {}).forEach((uploadId) => {
                    if (messageInput.removeFile) {
                      messageInput.removeFile(uploadId)
                    }
                  })

                  Object.keys(messageInput.imageUploads || {}).forEach((uploadId) => {
                    if (messageInput.removeImage) {
                      messageInput.removeImage(uploadId)
                    }
                  })
                }}
                className="text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            </div>
          ) : null
        })()}
      </div>
    </div>
  )
}

export default RichTextMessagingInput
